/**
 * 安聆 SafeListen - AI 音频监测模块
 * 麦克风采集 → Web Audio 分析 → 灵敏度阈值触发警报（声音+震动+UI 警示）
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Slider, message } from 'antd';
import { analyzeAudioData, playAlarmTone, triggerVibrate } from '../utils/audioUtils';
import './AudioMonitor.css';

const SENSITIVITY_MIN = 0.01;
const SENSITIVITY_MAX = 0.1;
const SENSITIVITY_STEP = 0.01;
const SENSITIVITY_DEFAULT = 0.05;

export default function AudioMonitor({ isMonitor, onMonitoringChange, onAlert }) {
  const { t } = useTranslation();
  const [sensitivity, setSensitivity] = useState(SENSITIVITY_DEFAULT);
  const [monitoring, setMonitoring] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const lastAlarmRef = useRef(0);

  const stopMonitoring = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (_) {}
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setMonitoring(false);
    setAlerting(false);
  }, []);

  const runAnalysis = useCallback(() => {
    const analyser = analyserRef.current;
    const ctx = ctxRef.current;
    if (!analyser || !ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Float32Array(bufferLength);
    const bufferSize = 2048;

    const tick = () => {
      analyser.getFloatTimeDomainData(dataArray);
      const buffer = ctx.createBuffer(1, bufferLength, ctx.sampleRate);
      buffer.getChannelData(0).set(dataArray);
      const level = analyzeAudioData(buffer);

      if (level > sensitivity) {
        const now = Date.now();
        if (now - lastAlarmRef.current > 2000) {
          lastAlarmRef.current = now;
          setAlerting(true);
          onAlert?.();
          playAlarmTone(ctx);
          triggerVibrate([200, 100, 200]);
          setTimeout(() => setAlerting(false), 1500);
        }
      }

      animationRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, [sensitivity]);

  const startMonitoring = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      analyserRef.current = analyser;
      setMonitoring(true);
    } catch (e) {
      message.error(t('audio.micDenied'));
    }
  }, [t, runAnalysis]);

  useEffect(() => {
    if (!monitoring) return;
    runAnalysis();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [monitoring, runAnalysis]);

  useEffect(() => {
    onMonitoringChange?.(monitoring);
  }, [monitoring, onMonitoringChange]);

  useEffect(() => () => stopMonitoring(), [stopMonitoring]);

  if (!isMonitor) return null;

  return (
    <div className={`safelisten-card audio-monitor ${alerting ? 'alerting' : ''}`}>
      <div className="sensitivity-row">
        <span className="sensitivity-label">{t('audio.sensitivityLabel')}</span>
        <Slider
          min={SENSITIVITY_MIN}
          max={SENSITIVITY_MAX}
          step={SENSITIVITY_STEP}
          value={sensitivity}
          onChange={setSensitivity}
          disabled={monitoring}
          marks={{
            [SENSITIVITY_MIN]: '0.01',
            [SENSITIVITY_DEFAULT]: '0.05',
            [SENSITIVITY_MAX]: '0.1',
          }}
        />
        <p className="sensitivity-hint">{t('audio.sensitivityHint')}</p>
      </div>
      <div className="monitor-actions">
        <Button
          type="primary"
          className="safelisten-btn-primary"
          onClick={monitoring ? stopMonitoring : startMonitoring}
        >
          {monitoring ? t('audio.stopMonitoring') : t('audio.startMonitoring')}
        </Button>
      </div>
      <div className="monitor-status">
        <span className={monitoring ? 'status-on' : 'status-off'}>
          {monitoring ? t('audio.monitoring') : t('audio.notMonitoring')}
        </span>
      </div>
      {alerting && <div className="alert-overlay" />}
    </div>
  );
}
